/**
 * Sky gradient dome (issue #60): replaces the flat `scene.background`
 * colour with a cheap vertical gradient — horizon haze at the equator
 * fading to a deeper zenith blue overhead — so the horizon is no longer a
 * hard silhouette edge. Colours come from scene/atmosphere.ts's single
 * palette, the same source fade.ts (fog) and lighting.ts (sun/ambient) use,
 * so sky/fog/sun never drift out of sync.
 *
 * One draw call: geometry + per-vertex colours are built once at
 * construction (mirrors terrain/terrainMaterial.ts's computeTintColors —
 * a pure function over geometry attributes, unit-tested directly in Node),
 * then wrapped in a three.js Mesh that main.ts recentres on the camera each
 * frame (a single position set, no allocation, no Hillaire/Bruneton LUT —
 * see the mining report outputs/qa/mining/05_visual_quality.md §4).
 */
import { BackSide, BufferAttribute, BufferGeometry, Color, Mesh, MeshBasicMaterial, SphereGeometry } from 'three';
import { ATMOSPHERE_PALETTE, rgbToHex, type AtmospherePalette } from './atmosphere';

/** Comfortably inside main.ts's PerspectiveCamera far plane (40000 m) so the
 * dome never clips even while SkyDome.update() recentres it on the camera
 * every frame as the viewer roams the AOI. */
export const SKY_DOME_RADIUS_M = 35000;

/**
 * Normalises a dome-local vertex height to a 0 (horizon) .. 1 (zenith)
 * gradient parameter. Anything at or below the equator (y <= 0) clamps to
 * 0 — the dome's lower hemisphere sits behind/below real terrain and is
 * never seen, so it's flat horizon haze rather than a wasted gradient.
 */
export function elevationParameter(y: number, radius: number): number {
  if (radius <= 0) return 0;
  return Math.max(0, Math.min(1, y / radius));
}

/** Linear-interpolates the sky gradient between the horizon and zenith
 * colours at parameter `t` (see elevationParameter), clamping t to 0..1. */
export function skyGradientColor(t: number, horizon: Color, zenith: Color): Color {
  const clamped = Math.max(0, Math.min(1, t));
  return new Color().copy(horizon).lerp(zenith, clamped);
}

/**
 * Per-vertex RGB colour array for a sky dome's geometry — mirrors
 * terrain/terrainMaterial.ts's computeTintColors shape (pure function over
 * geometry attributes, no renderer required).
 */
export function computeSkyVertexColors(
  geometry: BufferGeometry,
  radius: number,
  palette: AtmospherePalette = ATMOSPHERE_PALETTE,
): Float32Array {
  const position = geometry.getAttribute('position');
  if (!position) throw new Error('geometry has no position attribute');

  const horizon = new Color(rgbToHex(palette.horizon));
  const zenith = new Color(rgbToHex(palette.zenith));
  const colors = new Float32Array(position.count * 3);
  for (let i = 0; i < position.count; i++) {
    const t = elevationParameter(position.getY(i), radius);
    const c = skyGradientColor(t, horizon, zenith);
    colors[i * 3] = c.r;
    colors[i * 3 + 1] = c.g;
    colors[i * 3 + 2] = c.b;
  }
  return colors;
}

export class SkyDome {
  readonly mesh: Mesh;

  constructor(palette: AtmospherePalette = ATMOSPHERE_PALETTE, radius: number = SKY_DOME_RADIUS_M) {
    const geometry = new SphereGeometry(radius, 16, 12);
    geometry.setAttribute('color', new BufferAttribute(computeSkyVertexColors(geometry, radius, palette), 3));
    const material = new MeshBasicMaterial({
      vertexColors: true,
      side: BackSide,
      depthWrite: false,
      fog: false, // the dome already paints the horizon haze; scene.fog re-fogging it would double up
      toneMapped: false, // the palette is already authored as final display colour, not scene-linear light
    });
    this.mesh = new Mesh(geometry, material);
    this.mesh.renderOrder = -1;
    this.mesh.matrixAutoUpdate = false;
    this.mesh.updateMatrix();
  }

  /** Recentres the dome on the camera every frame so it always fully
   * encloses the visible scene regardless of how far the camera roams from
   * the local origin. Cheap: one position set + matrix update, no
   * allocation. */
  update(x: number, y: number, z: number): void {
    this.mesh.position.set(x, y, z);
    this.mesh.updateMatrix();
  }
}
